import { Exclude, Expose, Transform } from 'class-transformer';
import { map, omitBy } from 'lodash';

import { Page } from './page.model';

export class Search {

	table: string;
	jointable?: string;

	@Expose({ name: 'simple' })
	@Transform(prepareSimple, { toPlainOnly: true })
	simpleTmp: any = {};

	@Expose({ name: 'like' })
	@Transform(prepareLike, { toPlainOnly: true })
	likeTmp: any = {};

	@Expose({ name: 'fulltext' })
	@Transform(prepareLike, { toPlainOnly: true })
	fulltextTmp: any = {};

	@Expose({ name: 'range' })
	@Transform(prepareRange, { toPlainOnly: true })
	rangeTmp: any = {};

	@Expose({ name: 'notinrange' })
	@Transform(prepareRange, { toPlainOnly: true })
	notInRangeTmp: any = {};

	@Expose({ name: 'notequal' })
	@Transform(prepareNotEqual, { toPlainOnly: true })
	notEqualTmp: any = {};

	@Expose({ name: 'in' })
	@Transform(prepareIn, { toPlainOnly: true })
	inTmp: any = {};

	@Expose({ name: 'notin' })
	@Transform(prepareNotIn, { toPlainOnly: true })
	notInTmp: any = {};

	@Expose({ name: 'or' })
	@Transform(prepareOr, { toPlainOnly: true })
	orTmp: any = [];

	@Exclude()
	genericTmp: any = {};
	// generic filter -> field - value
	[key: string]: any
	[key: number]: any

	@Expose({ name: 'sortfield' })
	sortField?: string;
	@Expose({ name: 'direction' })
	sortDirection?: string;

	@Exclude()
	pageid?: string;
	@Exclude()
	page: Page = new Page();

	headers: {name: string; value: string}[] = [];

	constructor(table: string, pageid?: string) {
		this.table = table;
		this.pageid = pageid;
	}

	reset() {
		this.simpleTmp = {};
		this.likeTmp = {};
		this.fulltextTmp = {};
		this.rangeTmp = {};
		this.notEqualTmp = {};
		this.inTmp = {};
		this.notInTmp = {};
		this.orTmp = [];
		// this.notOrTmp = [];
		Object.keys(this.genericTmp).forEach(key => {
			this[key] = undefined;
		});
	}

	resetSort() {
		this.sortField = undefined;
		this.sortDirection = undefined;
	}

	addSimpleFilter(field: string, value: string | number | null) {
		this.simpleTmp[field] = value;
	}

	addLikeFilter(field: string, value: string | number, logic?: string) {
		this.likeTmp[field] = { value, logic };
	}

	addFulltextFilter(field: string, value: string) {
		this.likeTmp[field] = { value };
	}

	addRangeFilter(field: string, start: string | number, stop: string | number) {
		this.rangeTmp[field] = { start, stop };
	}

	addNotInRangeFilter(field: string, start: string | number, stop: string | number) {
		this.notInRangeTmp[field] = { start, stop };
	}

	addNotEqualFilter(field: string, value: string | number | null) {
		this.notEqualTmp[field] = value;
	}

	addInFilter(field: string, value: string[] | number[]) {
		this.inTmp[field] = value;
	}

	addNotInFilter(field: string, value: string[] | number[]) {
		this.notInTmp[field] = value;
	}

	addGenericFilter(field: string, value: string | number) {
		this[field] = value;
		this.genericTmp[field] = value;
	}

	addOrFilter(fields: string[], filter: string | number,
		unsensitive: boolean = false, like: boolean = false) {
		this.orTmp.push({ fields, filter, unsensitive, like });
	}

	addOrFilters(field: string, filters: string[] | number[],
		unsensitive: boolean = false, like: boolean = false) {
		this.orTmp.push({ fields: [field], filters, unsensitive, like });
	}

	setSortField(field: string) {
		this.sortField = field;
	}

	setSortDirection(direction: string) {
		this.sortDirection = direction;
	}

	removeSimpleFilter(field: string) {
		this.simpleTmp[field] = undefined;
		delete this.simpleTmp[field];
	}

	removeLikeFilter(field: string) {
		this.likeTmp[field] = undefined;
		delete this.likeTmp[field];
	}

	removeRangeFilter(field: string) {
		this.rangeTmp[field] = undefined;
		delete this.rangeTmp[field];
	}

	removeNotInRangeFilter(field: string) {
		this.notInRangeTmp[field] = undefined;
		delete this.notInRangeTmp[field];
	}

	removeOrFilter(field: string) {
		let index = -1;
		for (let i = 0; i < this.orTmp.length; i++) {
			if (this.orTmp[i].fields[0] === field) {
				index = i;
				break;
			}
		}
		if (index >= 0) {
			this.orTmp.splice(index, 1);
		}
	}

	addHeader(name: string, value: string) {
		this.headers.push({ name, value });
	}
}

export enum SortDirection {
	ASCENDING = 'asc',
	DESCENDING = 'desc'
}

export class RangeFilter {
	field!: string;
	from!: string;
	to!: string;
}

function prepareRange(value: any) {

	const output = map(value, (v: any, key: string) => ({
		field: key,
		start: v.start,
		stop: v.stop,
	}));

	return output;
}

function prepareSimple(value: any) {

	const output = map(value, (v: any, key: string) => ({
		field: key,
		filter: v,
		unsensitive: false
	}));

	return output;
}

function prepareLike(value: any) {

	const output = map(value, (v, key) => ({
		field: key,
		term: v.value,
		logic: v.logic,
	}));

	return output;
}

function prepareNotEqual(value: any) {

	const output = map(value, (v: any, key: string) => ({
		field: key,
		filter: v,
		unsensitive: false
	}));

	return output;
}

function prepareIn(value: any) {

	const output = map(value, (v: any[], key: string) => ({
		field: key,
		filter: v.join(';'),
		unsensitive: false
	}));

	return output;
}

function prepareNotIn(value: any) {

	const output = map(value, (v: any[], key: string) => ({
		field: key,
		filter: v.join(';'),
		unsensitive: false
	}));

	return output;
}

function prepareOr(value: any[]) {

	const output = map(omitBy(value, (v: any[]) => !v.length), (v: any, key: string) => ({
		fields: key,
		filter: v,
	}));

	return output;
}
